// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import useHttp from "hooks/use-http";

// material styles
import {
  Grid,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// react icons
import { MdDelete } from "react-icons/md";

// request methods
import {
  deleteArticleByIdRequest,
  getArticleByIdRequest,
  getArticleCategoryRequest,
  updateArticleRequest,
} from "api/requests/articles";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";
import DeleteModal from "components/shared/Modal/Delete";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";

// form validate
import { pageItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./editArticle.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import BackButton from "components/shared/BackButton";

export default function EditArticle() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();
  const { article_id } = useParams();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(pageItemSchema),
  });

  // editor value
  const [text, setText] = useState("");

  // upload image and video state
  const [articleImage, setArticleImage] = useState({
    url: null,
    isChanged: false,
  });
  const [articleVideo, setArticleVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // select option state
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // checkbox state
  const [checked, setChecked] = useState(false);

  const handleCheckChange = () => setChecked((state) => !state);

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //handle delete modal
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  // delete article by id
  const handleDeleteItem = useCallback(
    async (id) => {
      const res = await request({
        url: () => deleteArticleByIdRequest(id),
      });
      if (res) {
        Toast({ type: "success", title: "حذف اخبار " });
        navigate("/articles");
      }
      handelShowModal();
    },
    [setLoading]
  );

  // edit article
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!articleImage.url) return Toast({ type: "empty", title: "فیلد عکس " });

    const res = await request({
      url: () =>
        updateArticleRequest(
          {
            id: article_id,
            title: data.title,
            category_id: selectedCategory,
            text,
            alt: data.alt,
            meta_description: data.meta_description,
            keyword: data.keyword,
            show_in_home_page: checked ? 1 : 0,
            main_image: articleImage,
            video: articleVideo,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "ویرایش اخبار" });
      navigate("/articles");
    }
  };

  // uploaded image handler method
  const handleChangeArticleImage = (val) => {
    validateImg(val);
    setArticleImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeArticleVideo = (val) =>
    setArticleVideo({ url: val, isChanged: true });

  // get article by id data
  const getArticleByIdData = useCallback(async () => {
    setLoading(true);
    const categoryRes = await request({
      url: getArticleCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });

    if (categoryRes) {
      let transformedData = categoryRes.data?.data.map((items) => ({
        id: items.id,
        title: items.title,
      }));
      setCategoryList(transformedData);
    }

    const articleRes = await request({
      url: () => getArticleByIdRequest({ id: article_id }),
      isMulty: true,
    });

    if (articleRes) {
      let data = articleRes?.data;
      data.text && setText(data.text);
      setArticleImage({ url: data?.main_image, isWeb: true });
      if (data.video) {
        setArticleVideo({ url: data?.video, isWeb: true });
      }
      setChecked(data?.show_in_home_page);
      reset({
        title: data?.title,
        alt: data?.image_details?.alt,
        meta_description: data?.seo_options?.meta_description,
        keyword: data?.seo_options?.keyword,
      });

      if (data.category_id) {
        if (data.category_id) {
          setSelectedCategory(data.category.id);
          reset({
            category: data.category.id,
            alt: data?.image_details?.alt,
            meta_description: data?.seo_options?.meta_description,
            keyword: data?.seo_options?.keyword,
            title: data?.title,
          });
        }
      }
      setLoading(false);
    } else return Toast({ type: "err", title: "دریافت اطلاعات اخبار" });
  }, [request]);

  useEffect(() => {
    getArticleByIdData();
  }, [getArticleByIdData]);

  return (
    <DrawerCM title="ویرایش اخبار">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/articles" />
          </Grid>
          <StyledStack>
            <StyledTitle>ویرایش اخبار</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان خبر"
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="سردسته"
                  fullWidth
                  control={control}
                  title="سردسته"
                  id="category"
                  {...register("category")}
                  error={Boolean(errors.category)}
                  helperText={errors.category?.message}
                  onChange={(value) => setSelectedCategory(value)}
                >
                  {categoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2} sx={{ pt: 2.2 }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleCheckChange}
                      />
                    }
                    label="نمایش در صفحه اصلی"
                  />
                </FormGroup>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {articleImage.url ? (
                  <Upload
                    isWeb={articleImage.isWeb}
                    isLocal={!articleImage.isWeb}
                    file={articleImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeArticleImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={articleImage.isChanged}
                    file={articleImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeArticleImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {articleVideo.url ? (
                  <UploadVideoCM
                    isWeb={articleVideo.isWeb}
                    isLocal={!articleVideo.isWeb}
                    file={articleVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeArticleVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={articleVideo.isChanged}
                    file={articleVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeArticleVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                {text && (
                  <Editor
                    enableToolbar={true}
                    onChange={handleChangeText}
                    defaultValue={text}
                    setDefaultStyle="font-family: IranSans; height: 40vh;"
                    setOptions={{
                      buttonList: buttonList.complex,
                      font: [
                        "IranSans",
                        "IranSansBold",
                        "Arial",
                        "Impact",
                        "Tahoma",
                      ],
                    }}
                  />
                )}
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1, mr: 2 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
                <Button
                  startIcon={<MdDelete />}
                  variant="contained"
                  color="error"
                  sx={{ mt: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handelShowModal();
                  }}
                >
                  حذف
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={handleDeleteItem.bind(null, article_id)}
          />
        </Grid>
      )}
    </DrawerCM>
  );
}
